import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { produce } from 'immer';
import { Draft, ProductType, ProductTypeUpsellingAssortment } from '@/types';
import { trackFeature, updateProduct } from '@/utils/adobeLaunch';
import { fetchUpsellingProductTypes } from '@/utils/http/fetchUpsellingProductTypes';
import { t } from '@/utils/i18n';
import { isCYOMode } from '@/utils/params';
import { useIsSmall } from '@/hooks/useMediaQuery';
import { usePriceFormatter } from '@/hooks/usePrice';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getConfigurationsOnView } from '@/model/draft/getConfigurationsOnView';
import { getProductTypePrice } from '@/model/pricing/getProductTypePrice';
import { extendPrintTypeData } from '@/model/productType/extendPrintTypeData';
import { getClosestAppearance } from '@/model/productType/getClosestAppearance';
import { getIsBlankVerboten } from '@/model/productType/getIsBlankVerboten';
import { getPrintAreaForView } from '@/model/productType/getPrintAreaForView';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getDraft } from '@/redux/draft/getters/getDraft';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { getSelectedSizes } from '@/redux/draft/getters/getSelectedSizes';
import { getPrintTypes } from '@/redux/printTypes/getters/getPrintTypes';
import { productTypeSwitchAction } from '@/redux/product-type/actions/productTypeSwitchAction';
import { StoreDispatch, StoreState } from '@/redux/types';
import { Viewer } from '@/components/draft/Viewer';
import { setWarningAction } from '@/components/draft/draftState/actions/setWarningAction';
import { switchProductTypeAction } from '@/components/draft/draftState/actions/switchProductTypeAction';
import { StaticAppearance } from '../../Appearance';
import { Button } from '../../Button';
import { ProductTypeRating } from '../../ProductTypeRating';
import { Close } from '../../icons/Close';
import './UpsellingModal.scss';

export const INLINE_UPSELLING_CONTAINER = 'inline-upselling-container';

const CRITERIA_SORTING_VALUE: Record<string, number> = {
  PREMIUM_TO_ORGANIC: 1,
  STANDARD_TO_PREMIUM: 2,
};
const getCriteriaSortingValue = (criteria: string) => CRITERIA_SORTING_VALUE[criteria] || 9;
const sortByCriteria = (a: ProductTypeUpsellingAssortment, b: ProductTypeUpsellingAssortment) =>
  getCriteriaSortingValue(a.criteria) - getCriteriaSortingValue(b.criteria);

const areSelectedSizesAvailable = (
  selectedSizes: Record<string, number>,
  stockStates: ProductType['stockStates'],
  appearanceId: string
) =>
  Object.entries(selectedSizes).every(
    ([sizeId, quantity]) =>
      !quantity ||
      stockStates.find(
        (stockState) => stockState.appearance.id === appearanceId && stockState.size.id === sizeId
      )?.available
  );

function useUpsellingContainer() {
  const isSmall = useIsSmall();
  const container = document.getElementById(
    isSmall ? INLINE_UPSELLING_CONTAINER : 'modal-container'
  );

  // force a re-render if the container is not immediately available
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!container) {
      forceRender(Date.now());
    }
  }, [container]);

  return container;
}

interface UpsellingModalProps {
  onVisible: () => void;
  onClose: () => void;
}

const UpsellingModal: FunctionComponent<UpsellingModalProps> = ({ onVisible, onClose }) => {
  const store = useStore<StoreState>();
  const [upsellingDraft, setUpsellingDraft] = useState<Draft | undefined>();
  const storeDispatch = useDispatch<StoreDispatch>();
  const productType = useSelector(getProductType);
  const priceFormatter = usePriceFormatter();
  const selectedSizes = useSelector(getSelectedSizes);
  const priceData = useMemo(() => {
    let diff = 0;
    let priceUpsellingProductType = 0;

    if (upsellingDraft) {
      const priceCurrentProductType = getProductTypePrice(store.getState(), productType);
      priceUpsellingProductType = getProductTypePrice(store.getState(), upsellingDraft.productType);
      diff = priceUpsellingProductType - priceCurrentProductType;
    }

    return { diff, upselling: priceUpsellingProductType };
  }, [store, productType, upsellingDraft]);

  const appearanceCount = upsellingDraft?.productType.appearances.length;
  const pivot = appearanceCount ? (appearanceCount > 2 ? 2 - 1 : 2) : 0;
  const hiddenAppearances = upsellingDraft?.productType.appearances.slice(pivot);
  const draftDispatch = useReduxDraftDispatch();

  const lastProductType = useRef(productType);
  useEffect(() => {
    if (lastProductType.current !== productType) {
      onClose();
    }
  }, [productType, onClose]);

  useEffect(() => {
    if (
      upsellingDraft &&
      !areSelectedSizesAvailable(
        selectedSizes,
        upsellingDraft.productType.stockStates,
        upsellingDraft.appearance.id
      )
    ) {
      onClose();
    }
    // changes to upsellingDraft should not trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSizes, onClose]);

  useEffect(() => {
    fetchUpsellingProductTypes(productType.id)
      .then((upsellingResult) => {
        // some criteria are more important than others and should be used first
        upsellingResult.sort(sortByCriteria);

        const state = store.getState();
        const draft = getDraft(state);

        for (const upsellingAssortment of upsellingResult) {
          for (const upsellingProductType of upsellingAssortment.relatedProductTypes) {
            const closestAppearance = getClosestAppearance(
              upsellingProductType,
              getCurrentAppearance(state)
            );
            if (
              closestAppearance &&
              areSelectedSizesAvailable(
                draft.selectedSizes,
                upsellingProductType.stockStates,
                closestAppearance.id
              ) &&
              (!getIsBlankVerboten(upsellingProductType) || draft.configurationIds.length > 0)
            ) {
              extendPrintTypeData(upsellingProductType, getPrintTypes(state));
              setUpsellingDraft(
                produce(draft, switchProductTypeAction(upsellingProductType, closestAppearance))
              );
              onVisible();
              trackFeature('upselling-product-shown', {
                upsellingProductTypeId: upsellingProductType.id,
              });
              return;
            }
          }
        }

        // close the panel if no upselling product type can be used
        onClose();
      })
      .catch((e) => console.error(e));
  }, [productType, store, onVisible, onClose]);

  const container = useUpsellingContainer();

  if (!upsellingDraft || !container) return null;

  const handleProductTypeSelect = async () => {
    const productTypeId = upsellingDraft.productType.id;
    const appearanceId = upsellingDraft.appearance.id;

    try {
      storeDispatch(
        productTypeSwitchAction(productTypeId, appearanceId, undefined, () => {
          updateProduct({
            productTypeId,
            appearanceId,
            price: priceData.upselling,
          });
          trackFeature('upselling-product-select');
        })
      );

      draftDispatch(setWarningAction('UpsellingSelected', 'upsellingModal.hint'));
    } catch (error) {
      // ignore product type loading error
    }

    onClose();
  };

  return createPortal(
    <div className="upselling-modal">
      <div className="upselling-headline">{t('upsellingModal.headline')}</div>
      <div className="upselling-modal-inner">
        <div className="upselling-img-container">
          <Viewer
            printArea={getPrintAreaForView(upsellingDraft.productType, upsellingDraft.view)}
            view={upsellingDraft.view}
            appearance={upsellingDraft.appearance}
            configurations={getConfigurationsOnView(
              getConfigurations(upsellingDraft),
              upsellingDraft.view
            )}
            className="upselling-img"
            productTypeImageSize={250}
          />
        </div>
        <div>
          <div className="upselling-modal__name">{upsellingDraft.productType.name}</div>
          <ProductTypeRating
            productType={upsellingDraft.productType}
            className="product-type-ratings__upselling"
          />
          <div className="upselling-modal__appearances">
            <StaticAppearance appearance={upsellingDraft.appearance}>
              <span className="upselling-modal__appearance">{upsellingDraft.appearance.name}</span>
            </StaticAppearance>
            {hiddenAppearances && (
              <p className="upselling-modal__hidden-appearances">+{hiddenAppearances.length}</p>
            )}
          </div>
          <div className="upselling-modal__price">
            <strong>+{priceFormatter(priceData.diff)} </strong>
            {t('upsellingModal.price.noDiscount')}
          </div>
          <Button light className="upselling-change-product" onClick={handleProductTypeSelect}>
            {t('productTypeSelector.dialog.apply')}
          </Button>
        </div>
      </div>
      <button className="upselling-close" onClick={onClose}>
        <Close className="upselling-close-icon" />
      </button>
    </div>,
    container
  );
};

// only show this once per designer visit
let hasShownUpsellingOnce = false;
const showUpsellingOnce = () => {
  hasShownUpsellingOnce = true;
};

export function useUpselling(activate: boolean): ReactNode {
  const [showUpselling, setShowUpselling] = useState(false);
  const onClose = useCallback(() => setShowUpselling(false), []);

  useEffect(() => {
    if (!hasShownUpsellingOnce && activate && isCYOMode()) {
      setShowUpselling(true);
    }
  }, [activate]);

  return showUpselling ? <UpsellingModal onVisible={showUpsellingOnce} onClose={onClose} /> : null;
}
